import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CircularProgress,
  Typography
} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import axios from 'src/utils/axios';
import { getDateTimeToString } from 'src/utils/date';
import { COLORS, SENSOR_TYPES } from 'src/constants';
import {
  Droplet as WaterIcon,
  Zap as ElectricityIcon,
  Cloud as AirhumidityIcon,
  Sun as HeatenergyIcon,
  Truck as EVchargingIcon
} from 'react-feather';
import { makeStyles } from '@mui/styles';
import { formatDateTime } from 'src/utils/formatDateTime';
import Icon from 'src/components/Icon';


const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  label: {
    marginLeft: theme.spacing(1)
  },
  avatar: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    height: 48,
    width: 48
  },
  table: {
    border: 'none'
  },
  tablecell: {
    border: 'none',
    padding: '0px'
  }


}));


function LatestReceivedMeasurementValues({ className, ...rest }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const [latestValues, setLatestValues] = useState(null);
  const [progress, setProgress] = useState(30);
  const [isLoadingLast, setIsLoadingLast] = useState(false);

  const getLatestReceivedMeasurementValues = useCallback(async () => {
    setIsLoadingLast(true);
    setLatestValues([]);

    await axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/metering/GetLastReceivedValues`)
      .then((response) => {
        if (isMountedRef.current) {
          //  console.log(response.data); 
          setLatestValues(response.data);
        }
      });

    setIsLoadingLast(false);
    setProgress(30);
  }, [isMountedRef]);


  useEffect(() => {
    let intervalId = 0;
    getLatestReceivedMeasurementValues();
    intervalId = setInterval(() => getLatestReceivedMeasurementValues(), 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [getLatestReceivedMeasurementValues]);

  /*useEffect(() => {
    // Progressbar-animation
    const step = 100 / (30000 / 100); // uppdatera varje 100ms
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + step;
        return next >= 100 ? 100 : next;
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, []);*/

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setProgress((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000); // varje sekund

    return () => clearInterval(countdownInterval);
  }, []);

  /*function getSensorIcon(sensortype, index) {
    switch (sensortype) {
      case SENSOR_TYPES.COLDWATER:
        return [<WaterIcon key={index} fontSize="small" color="blue" />];
      case SENSOR_TYPES.HOTWATER:
        return [<WaterIcon key={index} fontSize="small" color="red" />];
      case SENSOR_TYPES.ELECTRICITY:
        return [<ElectricityIcon key={index} fontSize="small" color="yellow" />];
      case SENSOR_TYPES.AIRHUMIDITY:
        return [<AirhumidityIcon key={index} fontSize="small" />];
      case SENSOR_TYPES.HEATENERGY:
        return [<HeatenergyIcon key={index} fontSize="small" color="red" />];
      case SENSOR_TYPES.EVCHARGING:
        return [<EVchargingIcon key={index} fontSize="small" />];
      default:
        return '';
    }
  }*/
  
  function getSensorIcon(sensortype) {
    if (sensortype < 0) {
      switch (-sensortype) {
        case 1:
          return <Icon icon="droplet" color={COLORS.INDOORTEMP} />;
        case 2:
          return <Icon icon="droplet" color={COLORS.OUTDOORTEMP} />;
        case 3:
          return <Icon icon="thermometer-half" color={COLORS.REFTEMP} />;
        default:
          return '';
      }
    }

    switch (sensortype) {
      case SENSOR_TYPES.COLDWATER:
        return <Icon icon="droplet" color={COLORS.COLDWATER} />;
      case SENSOR_TYPES.HOTWATER:
        return <Icon icon="droplet" color={COLORS.HOTWATER} />;
      case SENSOR_TYPES.ELECTRICITY:
        return <Icon icon="bolt" color={COLORS.ELECTRICITY} />;
      case SENSOR_TYPES.AIRHUMIDITY:
        return <Icon icon="droplet-percent" color={COLORS.AIRHUMIDITY} />;
      case SENSOR_TYPES.HEATENERGY:
        return <Icon icon="fire-flame-curved" color={COLORS.HEATENERGY} />;
      case SENSOR_TYPES.EVCHARGING:
        return <Icon icon="car-bolt" color={COLORS.EVCHARGING} />;
      default:
        return '';
    }
  }

  if (!latestValues) {
    return null;
  }

  return (
    <Card
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Box flexGrow={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          component="h3"
          gutterBottom
          variant="overline"
          color="textSecondary"
        >
          Senast inkomna mätvärden
        </Typography>
        <Box position="relative" display="inline-flex">
          <CircularProgress
            variant="determinate"
            value={((30 - progress) / 30) * 100}
            size={35}
            thickness={5}
          />
          <Box
            top={0}
            left={0}
            bottom={0}
            right={0}
            position="absolute"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {`${Math.round(progress)}s`}
            </Typography>
          </Box>
        </Box>
      </Box>

        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
        >

          <TableContainer>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tablecell}>
                    Typ
                  </TableCell>
                  <TableCell className={classes.tablecell}>
                    Fastighetsägare
                  </TableCell>
                  <TableCell className={classes.tablecell}>
                    Fastighet
                  </TableCell>
                  <TableCell className={classes.tablecell}>
                    Tidpunkt
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {latestValues.map((row) => (
                  <TableRow key={row.timestamp} value={row}>
                    <TableCell className={classes.tablecell}>
                      {getSensorIcon(row.sensortype, row.timestamp)}
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {row.propertyOwnerName}
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {row.propertyName}
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {formatDateTime(row.timestamp)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>


        </Box>


      </Box>
    </Card>
  );
}

LatestReceivedMeasurementValues.propTypes = {
  className: PropTypes.string
};

export default LatestReceivedMeasurementValues;
